// Static legal / policy content for the MeraStartup marketing site.
// Company: GROCODE  ·  Product: MeraStartup

export const COMPANY = {
  product: "MeraStartup",
  name: "GROCODE",
  owner: "Nilesh Rajendra Barnawal",
  phone: "+91-9137287747",
  phoneHref: "+919137287747",
  email: "merastartup@grocode.in",
  address: "Raghuleela Mall, Kandivali West, Mumbai 400067",
};

const LAST_UPDATED = "July 2025";

// Each policy: { key, path, title, label, intro, blocks:[{h, p?:[], list?:[]}] }
export const POLICIES = [
  {
    key: "privacy",
    path: "/privacy-policy",
    label: "Privacy Policy",
    title: "Privacy Policy",
    intro: `This Privacy Policy explains how ${COMPANY.name} ("we", "us", "our"), the company behind ${COMPANY.product}, collects, uses and protects your information when you use our website and services.`,
    blocks: [
      { h: "Information we collect", list: [
        "Account details you provide — your name, email address and phone number.",
        "Store details you create such as your store name and preferred web address (slug).",
        "Payment information, which is processed securely by our payment partners (e.g. Cashfree, Razorpay). We do not store your full card or banking details on our servers.",
        "Usage and device data such as pages visited, browser type and approximate location, collected via cookies and analytics tools.",
      ]},
      { h: "How we use your information", list: [
        "To create, provision and operate your online store and (if purchased) your mobile app.",
        "To process payments, subscriptions and renewals.",
        "To send you important service messages, receipts and support responses.",
        "To improve our platform, prevent fraud and comply with legal obligations.",
      ]},
      { h: "Cookies & analytics", p: [
        "We use cookies and third-party analytics (such as Google Analytics) and advertising pixels (such as Meta Pixel and Google Ads) to understand how visitors use our site and to measure marketing performance. You can control cookies through your browser settings.",
      ]},
      { h: "Sharing of information", p: [
        "We do not sell your personal information. We share it only with trusted service providers (payment gateways, email, hosting and analytics) strictly to run our services, and when required by law.",
      ]},
      { h: "Data security", p: [
        "We use industry-standard measures including encryption in transit and hashed passwords to protect your data. However, no method of transmission over the internet is 100% secure.",
      ]},
      { h: "Your rights", p: [
        `You may request access to, correction of, or deletion of your personal data by writing to us at ${COMPANY.email}.`,
      ]},
      { h: "Contact us", p: [
        `For any privacy questions, contact ${COMPANY.name} at ${COMPANY.email} or ${COMPANY.phone}.`,
      ]},
    ],
  },
  {
    key: "terms",
    path: "/terms",
    label: "Terms & Conditions",
    title: "Terms & Conditions",
    intro: `These Terms & Conditions govern your use of ${COMPANY.product}, a service provided by ${COMPANY.name}. By accessing or using our website and services, you agree to these terms.`,
    blocks: [
      { h: "Our services", p: [
        "MeraStartup provides an instant ecommerce website builder and, optionally, a branded Android application, delivered through automated server provisioning.",
      ]},
      { h: "Subscriptions & billing", list: [
        "Plans are billed in advance for the period shown at checkout (for example, ₹1999/year for the Website plan).",
        "Prices are inclusive or exclusive of applicable taxes (GST) as shown at checkout.",
        "Your subscription remains active until its expiry date; on expiry your store and admin access may be locked until renewed.",
      ]},
      { h: "Acceptable use", p: [
        "You agree not to use the platform for any unlawful, infringing, or harmful purpose, and you are responsible for the products, content and legal compliance of your own store.",
      ]},
      { h: "Intellectual property", p: [
        "All platform software, branding and content owned by GROCODE remain our property. Content you upload remains yours, and you grant us a licence to host and display it to run your store.",
      ]},
      { h: "Refunds", p: [
        "Because our products are delivered instantly and digitally, fees are non-refundable once the service is provisioned. Please read our Return & Refund Policy for details.",
      ]},
      { h: "Limitation of liability", p: [
        "Our services are provided on an \"as is\" basis. To the maximum extent permitted by law, GROCODE is not liable for any indirect or consequential loss, including loss of sales or data.",
      ]},
      { h: "Governing law", p: [
        "These terms are governed by the laws of India and subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra.",
      ]},
      { h: "Contact us", p: [
        `Questions about these terms? Contact ${COMPANY.name} at ${COMPANY.email}.`,
      ]},
    ],
  },
  {
    key: "return",
    path: "/return-policy",
    label: "Return Policy",
    title: "Return & Refund Policy",
    intro: `This Return & Refund Policy applies to all purchases made on ${COMPANY.product} by ${COMPANY.name}.`,
    blocks: [
      { h: "No refunds on delivered products", p: [
        "We provide instant website creation, and the product is delivered instantly through automated server provisioning. Because the service is rendered and delivered immediately upon successful payment, we do not provide any refund once the product is sold and delivered.",
      ]},
      { h: "Digital, instant-delivery product", p: [
        "Our plans are digital services. As soon as your payment is confirmed, your store is created and made available to you — there is no way to \"return\" a store that has already been provisioned, so all sales are final.",
      ]},
      { h: "Exceptions", list: [
        "Accidental duplicate payments for the same order.",
        "Payment was captured but the service could not be provisioned due to a verified technical failure on our side.",
      ]},
      { h: "How to raise a concern", p: [
        `If you believe one of the exceptions above applies, contact us within 48 hours at ${COMPANY.email} or ${COMPANY.phone} with your order details. Approved cases are refunded to the original payment method within 5–7 business days.`,
      ]},
    ],
  },
  {
    key: "disclaimer",
    path: "/disclaimer",
    label: "Disclaimer",
    title: "Disclaimer",
    intro: `The information and services provided by ${COMPANY.name} through ${COMPANY.product} are offered in good faith and on an \"as is\" basis.`,
    blocks: [
      { h: "No guaranteed results", p: [
        "While our platform includes SEO-ready features and marketing tools, we do not guarantee any specific sales volume, search-engine ranking, traffic, or business outcome. Results depend on your products, pricing and marketing efforts.",
      ]},
      { h: "Merchant responsibility", p: [
        "Each merchant is solely responsible for the legality, accuracy and quality of the products, prices and content published on their own store, and for complying with applicable laws and taxes.",
      ]},
      { h: "Third-party links & services", p: [
        "Our platform integrates with third-party services (such as payment gateways and analytics providers). We are not responsible for the content, policies or availability of these third parties.",
      ]},
      { h: "Limitation", p: [
        "GROCODE shall not be held liable for any loss or damage arising from reliance on information or services provided through the platform.",
      ]},
    ],
  },
  {
    key: "shipping",
    path: "/shipping-policy",
    label: "Shipping Policy",
    title: "Shipping & Delivery Policy",
    intro: `${COMPANY.product} delivers digital products. This policy explains how and when your purchase is delivered.`,
    blocks: [
      { h: "Instant website delivery", p: [
        "Your ecommerce website is created instantly by our server automation as soon as your payment is confirmed. In most cases your store is live and accessible within moments of checkout.",
      ]},
      { h: "Mobile app delivery", p: [
        "If you purchase the Website + App plan, your branded Android app is built by our servers. App delivery may take up to 30 minutes depending on server run time and the current build queue. You will be able to download your app from your dashboard once the build completes.",
      ]},
      { h: "How delivery is confirmed", p: [
        "Access details and links are made available in your merchant dashboard and, where applicable, sent to your registered email address.",
      ]},
      { h: "No physical shipping", p: [
        "As all products are digital, there is no physical shipment, shipping charge, or courier involved.",
      ]},
      { h: "Need help?", p: [
        `If your website or app has not been delivered within the expected time, contact ${COMPANY.name} at ${COMPANY.email} or ${COMPANY.phone}.`,
      ]},
    ],
  },
  {
    key: "about",
    path: "/about",
    label: "About",
    title: "About us",
        intro: `Mera Startup (also written MeraStartup) is India's best Shopify alternative — an Indian ecommerce website builder. It is not a government startup-registration portal. ${COMPANY.product} is a product by ${COMPANY.name} — built so Indian shopkeepers can launch a store in minutes, without developers or hosting headaches.`,
    blocks: [
      { h: "What is MeraStartup?", p: [
        "Mera Startup is India's best Shopify alternative: a Shopify-style ecommerce website builder made for India. With one yearly plan you get a live store: cart, UPI and COD checkout, GST invoices, SEO pages, WhatsApp orders, and an optional branded Android app. People searching “mera startup” often mean this product — merastartup.in — not Startup India or company registration.",
      ]},
      { h: "Who we are", p: [
        `${COMPANY.name} is a technology company focused on making ecommerce accessible and affordable for small and growing Indian businesses. Through ${COMPANY.product}, a shopkeeper, maker or founder can get a full online store — with cart, payments, SEO and even their own Android app — for one honest yearly price.`,
      ]},
      { h: "Our mission", p: [
        "To put the power of a modern online store into every entrepreneur's hands: no coding, no middlemen, and no surprise renewals — just a store that is ready to take its first real order.",
      ]},
      { h: "Company details", list: [
        `Legal Owner: ${COMPANY.owner}`,
        `Company: ${COMPANY.name}`,
        `Registered office: ${COMPANY.address}`,
        `Email: ${COMPANY.email}`,
        `Phone: ${COMPANY.phone}`,
      ]},
      { h: "Meet the founder", p: [
        `${COMPANY.product} was founded by Neel Varma (legal name ${COMPANY.owner}). Read his story, contact details and other companies on the founder page.`,
      ]},
    ],
  },
  {
    key: "contact",
    path: "/contact",
    label: "Contact",
    title: "Contact Us",
    intro: `We'd love to hear from you. Reach ${COMPANY.name} using any of the details below.`,
    blocks: [
      { h: "Get in touch", list: [
        `Phone / WhatsApp: ${COMPANY.phone}`,
        `Email: ${COMPANY.email}`,
        `Office: ${COMPANY.address}`,
      ]},
      { h: "Support hours", p: [
        "Our team is available Monday to Saturday, 10:00 AM to 7:00 PM IST. We aim to respond to all email queries within one business day.",
      ]},
    ],
  },
  {
    key: "fb-data-deletion",
    path: "/fb-data-deletion",
    label: "FB Data Deletion",
    title: "Facebook Data Deletion",
    intro: `Request deletion of the data ${COMPANY.product} may have received about you through Facebook Login or any Meta platform integration.`,
    blocks: [],
  },
];

export const getPolicy = (key) => POLICIES.find((p) => p.key === key) || null;
